import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Menu, X, Phone, Clock, MapPin, ChevronDown, ChevronUp, ArrowRight,
  Home, Info, HelpCircle, BookOpen, FileText, Briefcase, Building, Store,
  ShoppingCart, CreditCard, User, Users, UserCircle, Heart, Mail,
  MessageCircle, Send, Activity, Thermometer, Pill, Syringe, HeartPulse,
  Calendar, Star, Settings, Bell, Gift, Share, ThumbsUp, Award, Globe, Search
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import axios from 'axios';

// Icon mapping for menu items
const iconMap = {
  Home, Menu, Search, ArrowRight, Info, HelpCircle, BookOpen, FileText,
  Briefcase, Building, Store, ShoppingCart, CreditCard, User, Users,
  UserCircle, Heart, Phone, Mail, MapPin, MessageCircle, Send,
  Activity, Thermometer, Pill, Syringe, HeartPulse, Calendar, Clock,
  Star, Settings, Bell, Gift, Share, ThumbsUp, Award, Globe,
  // Custom mappings for special icons (these will use existing icons as fallback)
  Stethoscope: Activity,
  PawPrint: Heart,
  Dog: Heart,
  Cat: Heart,
  Bird: Heart
};

// Helper to get icon component - icons use currentColor so they inherit text color
const getMenuIcon = (iconName, className = "h-4 w-4") => {
  if (!iconName || iconName === 'none') return null;
  const IconComponent = iconMap[iconName];
  if (!IconComponent) return null;
  // Icons inherit color from parent via currentColor (default behavior of Lucide icons)
  return <IconComponent className={className} />;
};

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeSubmenu, setActiveSubmenu] = useState(null); // Track second level submenu
  const [openSubSubmenus, setOpenSubSubmenus] = useState(new Set()); // Track multiple open third level submenus
  const [openMobileSubmenu, setOpenMobileSubmenu] = useState(null); // New state for mobile submenu
  const [openMobileSubSubmenus, setOpenMobileSubSubmenus] = useState(new Set()); // Track multiple open third level mobile submenus
  const [businessInfo, setBusinessInfo] = useState(null);
  const [currentHours, setCurrentHours] = useState(null);
  const [headerConfig, setHeaderConfig] = useState(null); // Header configuration from CMS
  const [notificationBarSettings, setNotificationBarSettings] = useState(null); // Notification bar settings
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAdmin, logout } = useAuth();
  const { 
    urgent_care_enabled, 
    registration_enabled, 
    online_ordering_enabled,
    online_appointments_enabled,
    services_enabled,
    boarding_enabled,
    records_enabled,
    hr_enabled,
    ai_costs_enabled,
    system_settings_enabled
  } = useSettings();
  
  // Permission checking helper
  const hasPermission = (path) => {
    // Admin always has full access (call isAdmin function)
    if (isAdmin()) return true;
    
    // Debug logging
    if (path === 'system_settings.enabled') {
      console.log('🔍 Checking system_settings.enabled permission:', {
        has_custom_role: user?.has_custom_role,
        permissions: user?.permissions?.system_settings,
        isAdminResult: isAdmin()
      });
    }
    
    // If user has custom role assigned (has_custom_role flag), check permissions
    if (user?.has_custom_role) {
      // User has a custom role - must check permissions
      if (!user.permissions) {
        console.warn('User has custom role but no permissions loaded');
        return false;
      }
      
      const pathParts = path.split('.');
      let current = user.permissions;
      
      for (const part of pathParts) {
        if (current && typeof current === 'object' && part in current) {
          current = current[part];
        } else {
          return false;
        }
      }
      
      const result = current === true;
      if (path === 'system_settings.enabled') {
        console.log('✅ Permission check result:', result);
      }
      return result;
    }
    
    // User has no custom role - allow access (backward compatibility for old users)
    if (!user?.has_custom_role) {
      console.log('⚠️ User has no custom role, allowing access (backward compatibility)');
    }
    return true;
  };
  
  // Handle logout with redirect to home page
  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Toggle mobile submenu
  const toggleMobileSubmenu = (itemName) => {
    setOpenMobileSubmenu(openMobileSubmenu === itemName ? null : itemName);
  };

  // Toggle sub-submenu (desktop) - allows multiple to be open
  const toggleSubSubmenu = (name) => {
    setOpenSubSubmenus(prev => {
      const newSet = new Set(prev);
      if (newSet.has(name)) {
        newSet.delete(name);
      } else {
        newSet.add(name);
      }
      return newSet;
    });
  };

  // Toggle mobile sub-submenu - allows multiple to be open
  const toggleMobileSubSubmenu = (name) => {
    setOpenMobileSubSubmenus(prev => {
      const newSet = new Set(prev);
      if (newSet.has(name)) {
        newSet.delete(name);
      } else {
        newSet.add(name);
      }
      return newSet;
    });
  };

  // Get backend URL with the same logic as AuthContext
  const getBackendURL = () => {
    if (process.env.REACT_APP_BACKEND_URL) {
      return process.env.REACT_APP_BACKEND_URL;
    }
    
    const origin = window.location.origin;
    if (origin.includes('walrus-app') || origin.includes('ondigitalocean.app')) {
      return origin + '/pandvah-backend';
    }
    
    if (origin.includes('localhost:3000')) {
      return 'http://localhost:8000';
    }
    
    return origin + '/pandvah-backend';
  };

  const API_BASE_URL = getBackendURL();

  // Fetch business information, current hours, and header config
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [businessResponse, hoursResponse, headerResponse, notificationResponse] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/business-info`),
          axios.get(`${API_BASE_URL}/api/hours/current`),
          axios.get(`${API_BASE_URL}/api/header-config`),
          axios.get(`${API_BASE_URL}/api/notification-bar-settings/public`)
        ]);
        setBusinessInfo(businessResponse.data);
        setCurrentHours(hoursResponse.data);
        if (headerResponse.data?.success) {
          setHeaderConfig(headerResponse.data.config);
        }
        setNotificationBarSettings(notificationResponse.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        // Fallback to default values
        setBusinessInfo({
          hospital_name: "Pets & Vets",
          address: "South Riding, VA 20152",
          phone: "(703) 957-3297"
        });
      }
    };

    fetchData();
  }, [API_BASE_URL]);

  // Get logo display settings from header config
  const logoDisplayMode = headerConfig?.logo_display_mode || 'logo_name_tagline';
  const logoTextAlign = headerConfig?.logo_text_align || 'left';
  
  // Helper to determine what to show
  const showLogo = !['name_only', 'name_tagline'].includes(logoDisplayMode);
  const showBusinessName = logoDisplayMode !== 'logo_only';
  const showTagline = ['logo_name_tagline', 'name_tagline'].includes(logoDisplayMode);

  // Render Logo Component
  const renderLogo = (size = 'normal') => {
    const logoSize = size === 'large' ? 'w-8 h-8' : 'w-7 h-7';
    const iconSize = size === 'large' ? 'w-6 h-6' : 'w-5 h-5';
    const plusWidth = size === 'large' ? '16px' : '14px';
    const plusHeight = size === 'large' ? '3px' : '3px';
    const plusHeightVert = size === 'large' ? '16px' : '14px';
    const plusWidthVert = size === 'large' ? '3px' : '3px';
    
    if (!showLogo) return null;
    
    const themeColor = headerConfig?.theme_color || 'var(--theme-primary)';
    
    if (businessInfo?.favicon_url) {
      return (
        <div className="rounded-lg p-1.5" style={{ backgroundColor: themeColor }}>
          <div className={`${logoSize} bg-white rounded flex items-center justify-center overflow-hidden`}>
            <img 
              src={businessInfo.favicon_url} 
              alt="Logo" 
              className={`${iconSize} object-contain`}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = `
                  <div class="relative">
                    <div style="width: ${plusWidth}; height: ${plusHeight}; background-color: ${themeColor}; position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);"></div>
                    <div style="width: ${plusWidthVert}; height: ${plusHeightVert}; background-color: ${themeColor}; position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);"></div>
                  </div>
                `;
              }}
            />
          </div>
        </div>
      );
    }
    
    return (
      <div className="rounded-lg p-1.5" style={{ backgroundColor: themeColor }}>
        <div className={`${logoSize} bg-white rounded flex items-center justify-center`}>
          <div className="relative">
            <div 
              className="absolute" 
              style={{
                width: plusWidth,
                height: plusHeight,
                backgroundColor: themeColor,
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)'
              }}
            ></div>
            <div 
              className="absolute" 
              style={{
                width: plusWidthVert,
                height: plusHeightVert,
                backgroundColor: themeColor,
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)'
              }}
            ></div>
          </div>
        </div>
      </div>
    );
  };

  // Render Business Name & Tagline
  const renderBusinessText = () => {
    if (!showBusinessName && !showTagline) return null;
    
    const textAlignClass = logoTextAlign === 'center' ? 'text-center' : 'text-left';
    
    return (
      <div className={textAlignClass}>
        {showBusinessName && (
          <h1 className="text-lg font-bold text-gray-900">
            {businessInfo?.website_title || businessInfo?.hospital_name || 'Pets & Vets'}
          </h1>
        )}
        {showTagline && (
          <p className="text-xs text-gray-600">
            {businessInfo?.website_subtitle || 'Animal Hospital & Urgent Care'}
          </p>
        )}
      </div>
    );
  };

  // Get menu display mode from config
  const menuDisplayMode = headerConfig?.menu_display_mode || 'text_only';
  
  // Get icon size from config
  const menuIconSize = headerConfig?.menu_icon_size || 'regular';
  const iconSizeClass = {
    'small': 'h-3.5 w-3.5',
    'regular': 'h-4 w-4',
    'medium': 'h-5 w-5',
    'large': 'h-6 w-6'
  }[menuIconSize] || 'h-4 w-4';
  
  // Get icon for a navigation item by name
  const getNavItemIcon = (itemName) => {
    const topMenus = headerConfig?.top_menus || [];
    const menuConfig = topMenus.find(m => m.label?.toLowerCase() === itemName?.toLowerCase());
    return menuConfig?.icon || 'none';
  };
  
  // Helper to render menu item content (icon + text based on display mode)
  const renderMenuContent = (label, iconName, hasDropdown = false) => {
    const icon = getMenuIcon(iconName, iconSizeClass);
    
    switch (menuDisplayMode) {
      case 'icon_only':
        return (
          <span className="inline-flex items-center">
            {icon || <span>{label}</span>}
            {hasDropdown && <ChevronDown className="ml-1 h-4 w-4" />}
          </span>
        );
      case 'icon_text':
        return (
          <span className="inline-flex items-center">
            {icon && <span className="mr-1.5 inline-flex items-center">{icon}</span>}
            <span>{label}</span>
            {hasDropdown && <ChevronDown className="ml-1 h-4 w-4" />}
          </span>
        );
      case 'text_icon':
        return (
          <span className="inline-flex items-center">
            <span>{label}</span>
            {icon && <span className="ml-1.5 inline-flex items-center">{icon}</span>}
            {hasDropdown && <ChevronDown className="ml-1 h-4 w-4" />}
          </span>
        );
      case 'text_only':
      default:
        return (
          <span className="inline-flex items-center">
            {label}
            {hasDropdown && <ChevronDown className="ml-1 h-4 w-4" />}
          </span>
        );
    }
  };

  // Function to get current time in business timezone
  const getCurrentBusinessTime = () => {
    if (!businessInfo?.timezone) {
      return new Date(); // Fallback to local time if timezone not available
    }
    
    try {
      // Create a date in the business timezone
      const now = new Date();
      const businessTime = new Date(now.toLocaleString("en-US", {timeZone: businessInfo.timezone}));
      return businessTime;
    } catch (error) {
      console.error('Error getting business time:', error);
      return new Date(); // Fallback to local time
    }
  };

  // Function to determine urgent care status with Opening Soon/Closing Soon
  const getUrgentCareStatus = () => {
    if (!currentHours || !currentHours.urgent_care) {
      return "Closed";
    }

    const urgentCareHours = currentHours.urgent_care;
    
    // Check if urgent care is closed today
    if (!urgentCareHours.is_open || !urgentCareHours.open_time || !urgentCareHours.close_time) {
      return "Closed";
    }

    const businessTime = getCurrentBusinessTime();
    const currentMinutes = businessTime.getHours() * 60 + businessTime.getMinutes();
    
    try {
      const openMinutes = parseTimeToMinutes(urgentCareHours.open_time);
      const closeMinutes = parseTimeToMinutes(urgentCareHours.close_time);
      
      // Check if currently open
      if (currentMinutes >= openMinutes && currentMinutes < closeMinutes) {
        // Check if closing soon (within 60 minutes)
        if (closeMinutes - currentMinutes <= 60) {
          return "Closing Soon";
        }
        return "Open";
      }
      
      // Check if opening soon (within 60 minutes)
      if (currentMinutes < openMinutes && openMinutes - currentMinutes <= 60) {
        return "Opening Soon";
      }
      
      return "Closed";
    } catch (error) {
      console.error('Error parsing urgent care hours:', error);
      return "Closed";
    }
  };

  // Function to determine general practice status with Opening Soon/Closing Soon
  const getGeneralPracticeStatus = () => {
    if (!currentHours || !currentHours.general_practice) {
      return "Closed";
    }

    const gpHours = currentHours.general_practice;
    
    // Check if general practice is closed today
    if (!gpHours.is_open || !gpHours.open_time || !gpHours.close_time) {
      return "Closed";
    }

    const businessTime = getCurrentBusinessTime();
    const currentMinutes = businessTime.getHours() * 60 + businessTime.getMinutes();
    
    try {
      const openMinutes = parseTimeToMinutes(gpHours.open_time);
      const closeMinutes = parseTimeToMinutes(gpHours.close_time);
      
      // Check if currently open
      if (currentMinutes >= openMinutes && currentMinutes < closeMinutes) {
        // Check if closing soon (within 60 minutes)
        if (closeMinutes - currentMinutes <= 60) {
          return "Closing Soon";
        }
        return "Open";
      }
      
      // Check if opening soon (within 120 minutes / 2 hours)
      if (currentMinutes < openMinutes && openMinutes - currentMinutes <= 120) {
        return "Opening Soon";
      }
      
      return "Closed";
    } catch (error) {
      console.error('Error parsing general practice hours:', error);
      return "Closed";
    }
  };

  // Helper function to parse time strings like "09:00" or "14:30" to total minutes
  const parseTimeToMinutes = (timeString) => {
    if (!timeString) return 0;
    
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + (minutes || 0);
  };

  // Function to get status styling based on status text
  const getStatusStyling = (status) => {
    // Simple white text styling that inherits font size and weight from parent
    return {
      color: 'white'
    };
  };

  // Get short address from business info
  const getShortAddress = () => {
    if (!businessInfo || !businessInfo.address) {
      return "South Riding, VA 20152";
    }
    
    // Extract city, state, zip from full address
    const address = businessInfo.address;
    const parts = address.split(',').map(part => part.trim());
    
    // Look for VA and zip code pattern and include the city before it
    for (let i = 0; i < parts.length; i++) {
      if (parts[i].includes('VA') || parts[i].includes('Virginia')) {
        // Get the city name (previous part) + state + zip
        let result = '';
        if (i > 0) {
          // Include city name from previous part
          result = parts[i - 1];
        }
        
        // Add state
        if (result) {
          result += `, ${parts[i]}`;
        } else {
          result = parts[i];
        }
        
        // Add zip code if available
        if (i + 1 < parts.length) {
          result += `, ${parts[i + 1]}`;
        }
        
        return result;
      }
    }
    
    // Fallback: return last three parts if available (city, state, zip)
    if (parts.length >= 3) {
      return `${parts[parts.length - 3]}, ${parts[parts.length - 2]}, ${parts[parts.length - 1]}`;
    } else if (parts.length >= 2) {
      return `${parts[parts.length - 2]}, ${parts[parts.length - 1]}`;
    }
    
    return "South Riding, VA 20152";
  };

  const getNavigation = () => {
    const baseNavigation = [
      // Only show Home button if user is not logged in
      ...(user ? [] : [{ name: 'Home', href: '/' }]),
      { 
        name: 'About',
        dropdown: [
          { name: 'Our Mission', href: '/our-mission' },
          { name: 'Our Values', href: '/our-core-values' },
          { name: 'Our Team', href: '/our-team' },
          { name: 'Our Facility', href: '/gallery' },
          { name: 'Review Us', href: '/review-us' }
        ]
      },
      // Only show Urgent Care button if user is not logged in and urgent care is enabled
      ...(!user && urgent_care_enabled ? [{ name: 'Urgent Care', href: '/urgent-care' }] : []),
      { 
        name: 'Services',
        dropdown: [
          ...(urgent_care_enabled ? [{ name: 'Urgent Care', href: '/urgent-care' }] : []),
          ...(!urgent_care_enabled ? [{ name: 'Specialty Services', href: '/specialty-services' }] : []),
          {
            name: 'Dog Services',
            submenu: [
              { name: 'Dog Dental Care', href: '/dog-dental-care' },
              { name: 'Dog Dermatology', href: '/dog-skin-care' },
              { name: 'Dog Vaccinations', href: '/dog-vaccinations' },
              { name: 'Dog Wellness', href: '/dog-wellness-exams' },
              { name: 'Dog Eye Care', href: '/dog-eye-care' },
              { name: 'Dog Surgeries', href: '/dog-surgeries' }
            ]
          },
          {
            name: 'Cat Services',
            submenu: [
              { name: 'Cat Dental Care', href: '/cat-dental-care' },
              { name: 'Cat Diagnostic Imaging', href: '/cat-diagnostic-imaging' },
              { name: 'Cat Vaccinations', href: '/cat-vaccinations' }
            ]
          },
          {
            name: 'General Services',
            submenu: [
              { name: 'Vaccinations', href: '/pet-vaccinations' },
              { name: 'Wellness Exams', href: '/preventive-pet-care' },
              { name: 'Ultrasounds', href: '/ultrasound-exams' },
              { name: 'Diagnostic Laboratory', href: '/veterinary-diagnostic-services' },
              { name: 'Dermatology & Allergy Care', href: '/pet-dermatology-allergy-care' },
              { name: 'Digital Radiology', href: '/digital-veterinary-x-rays' },
              { name: 'Microchipping', href: '/pet-microchipping' },
              { name: 'End of Life Care', href: '/end-of-life-care' },
              { name: 'On Site Pharmacy', href: '/on-site-pharmacy' },
              { name: 'Travel Certificates', href: '/pet-travel-certificates' },
              { name: 'Diet & Nutrition', href: '/dog-diet-nutrition' }
            ]
          },
          {
            name: 'Dental Services',
            submenu: [
              { name: 'Dental Cleaning', href: '/dental-cleanings' },
              { name: 'Tooth Extractions', href: '/pet-tooth-extraction' }
            ]
          },
          {
            name: 'Surgical Services',
            submenu: [
              { name: 'Spay & Neuter', href: '/pet-spay-neuter' },
              { name: 'Bladder Stone Removal', href: '/pet-bladder-stone-removal' },
              { name: 'Blocked Cat / PU Surgery', href: '/blocked-cats-urgent-care' },
              { name: 'Foreign Body / Obstruction Surgery', href: '/foreign-body-surgery' }
            ]
          },
          { name: 'Eye Care', href: '/pet-ocular-services' }
        ]
      },
      { 
        name: 'Patient',
        dropdown: [
          { name: 'Your First Visit', href: '/your-first-visit' },
          { name: 'Insurance & Payments', href: '/pet-insurance-payments' },
          { 
            name: 'Online Forms', 
            submenu: [
              { name: 'New Patient Registration', href: '/forms/new-patient-registration' },
              { name: 'Patient Drop-Off', href: '/forms/patient-drop-off' },
              { name: 'Surgery & Anesthesia Consent', href: '/forms/surgery-anesthesia-consent' },
              { name: 'Dental Consent', href: '/forms/dental-consent' },
              { name: 'Request Pet Records', href: '/forms/request-pet-records' }
            ]
          },
          { name: 'Download Forms', href: '/forms-download' }
        ]
      }
    ];

    // Add role-based menu only if user is logged in
    if (user) {
      const roleDropdown = [];
      
      // REMOVED: Old hardcoded admin block - now uses permission-based navigation below
      
      // NEW ARCHITECTURE: Build navigation based purely on permissions from custom roles
      // All users have custom roles assigned in Manage Roles with specific permissions
      // No hardcoded role checks - everything is permission-based
      
      // Build all menus based on permissions only (no role name checks)
      // This works for ANY custom role name (Manager, Staff, Supervisor, etc.)
      
      // Dashboards menu - check permission, not role
      const dashboardSubmenu = [];
      if (hasPermission('dashboards.hospital')) dashboardSubmenu.push({ name: 'Hospital', href: '/main-dashboard' });
      if (hasPermission('dashboards.employee')) dashboardSubmenu.push({ name: 'Employee', href: '/my-dashboard' });
      if (hasPermission('dashboards.web_analytics')) dashboardSubmenu.push({ name: 'Web Analytics', href: '/analytics-report' });
      if (hasPermission('dashboards.popup_analytics')) dashboardSubmenu.push({ name: 'Popup Analytics', href: '/landing-page-promotions' });
      if (online_ordering_enabled && hasPermission('online_ordering.manage_orders')) dashboardSubmenu.push({ name: 'Table Reservations', href: '/table-reservations-dashboard' });
      if (online_ordering_enabled && hasPermission('online_ordering.pending_orders')) dashboardSubmenu.push({ name: 'Orders', href: '/current-orders' });
      if (online_ordering_enabled && hasPermission('online_ordering.manage_orders')) dashboardSubmenu.push({ name: 'Counter', href: '/counter' });
          
          if (dashboardSubmenu.length > 0) {
            roleDropdown.push({ 
              name: 'Dashboards',
              submenu: dashboardSubmenu
            });
          }
          
          // Only show Online Appointments if enabled (Manager) and has permission
          if (online_appointments_enabled && hasPermission('online_appointments.enabled')) {
            const appointmentSubmenu = [];
            
            if (services_enabled) {
              const servicesSubmenu = [];
              if (hasPermission('online_appointments.services.book_online')) servicesSubmenu.push({ name: 'Book Online', href: '/book-appointment' });
              if (hasPermission('online_appointments.services.configure_service_providers')) servicesSubmenu.push({ name: 'Configure Service Providers', href: '/manage-appointment-calendar' });
              if (hasPermission('online_appointments.services.manage_services')) servicesSubmenu.push({ name: 'Manage Services', href: '/service-management' });
              if (hasPermission('online_appointments.services.manage_appointments')) servicesSubmenu.push({ name: 'Manage Appointments', href: '/manage-appointments' });
              
              // Sort by string length (ascending - shortest first)
              servicesSubmenu.sort((a, b) => a.name.length - b.name.length);
              
              if (servicesSubmenu.length > 0) {
                appointmentSubmenu.push({
                  name: 'Services',
                  submenu: servicesSubmenu
                });
              }
            }
            
            if (boarding_enabled) {
              const boardingSubmenu = [];
              if (hasPermission('online_appointments.boarding.book_online')) boardingSubmenu.push({ name: 'Book Online', href: '/book-boarding' });
              if (hasPermission('online_appointments.boarding.manage_appointments')) boardingSubmenu.push({ name: 'Manage Appointments', href: '/manage-appointments' });
              if (hasPermission('online_appointments.boarding.configure_boarding')) boardingSubmenu.push({ name: 'Configure Boarding', href: '/manage-boarding' });
              
              // Sort by string length (ascending - shortest first)
              boardingSubmenu.sort((a, b) => a.name.length - b.name.length);
              
              if (boardingSubmenu.length > 0) {
                appointmentSubmenu.push({
                  name: 'Boarding',
                  submenu: boardingSubmenu
                });
              }
            }
            
            if (urgent_care_enabled) {
              const urgentCareSubmenu = [];
              if (hasPermission('online_appointments.urgent_care.book_online')) urgentCareSubmenu.push({ name: 'Book Online', href: '/urgent-care-booking' });
              if (hasPermission('online_appointments.urgent_care.manage_appointments')) urgentCareSubmenu.push({ name: 'Manage Appointments', href: '/urgent-care-appointments' });
              
              if (urgentCareSubmenu.length > 0) {
                appointmentSubmenu.push({
                  name: 'Urgent Care',
                  submenu: urgentCareSubmenu
                });
              }
            }
            
            if (appointmentSubmenu.length > 0) {
              roleDropdown.push({
                name: 'Online Appointments',
                submenu: appointmentSubmenu
              });
            }
          }
          
          // Business menu
          const businessSubmenu = [];
          if (hasPermission('business.pdfs')) businessSubmenu.push({ name: 'PDFs', href: '/pdf-tool' });
          if (hasPermission('business.assets')) businessSubmenu.push({ name: 'Assets', href: '/manage-assets' });
          if (hasPermission('business.invoices')) businessSubmenu.push({ name: 'Invoices', href: '/invoices' });
          if (hasPermission('business.qr_codes')) businessSubmenu.push({ name: 'QR Codes', href: '/ai-qr-codes' });
          if (hasPermission('business.short_urls')) businessSubmenu.push({ name: 'Short URLs', href: '/ai-short-urls' });
          if (hasPermission('business.employee_schedules')) {
            businessSubmenu.push({ name: 'Clock In/Out', href: '/timesheet-clock' });
            businessSubmenu.push({ name: 'Employee Schedules', href: '/scheduling-calendar' });
          }
          
          if (businessSubmenu.length > 0) {
            roleDropdown.push({ 
              name: 'Business',
              submenu: businessSubmenu
            });
          }
          
          // Subscriptions menu
          const subscriptionsSubmenu = [];
          if (hasPermission('business.invoices')) {
            subscriptionsSubmenu.push({ name: 'Online Subscriptions', href: '/subscription-pricing' });
            subscriptionsSubmenu.push({ name: 'Manage Subscriptions', href: '/subscription-plans?tab=subscriptions' });
          }
          
          if (subscriptionsSubmenu.length > 0) {
            roleDropdown.push({ 
              name: 'Subscriptions',
              submenu: subscriptionsSubmenu
            });
          }
          
          // Website menu
          const websiteSubmenu = [];
          if (hasPermission('website.photos')) websiteSubmenu.push({ name: 'Photos', href: '/photo-management' });
          if (hasPermission('website.reviews')) websiteSubmenu.push({ name: 'Reviews', href: '/reviews' });
          if (hasPermission('website.business_hours')) websiteSubmenu.push({ name: 'Business Hours', href: '/configure-hours' });
          if (hasPermission('website.business_details')) websiteSubmenu.push({ name: 'Business Details', href: '/business-info' });
          if (hasPermission('website.landing_page_popups')) websiteSubmenu.push({ name: 'Landing Page Popups', href: '/landing-page-popups' });
          websiteSubmenu.push({ name: 'Content Manager', href: '/content-manager' });
          websiteSubmenu.push({ name: 'Notification Bar', href: '/notification-bar' });
          
          if (websiteSubmenu.length > 0) {
            roleDropdown.push({ 
              name: 'Website',
              submenu: websiteSubmenu
            });
          }
          
          // Only show HR if enabled (Manager) and has permission
          if (hr_enabled && hasPermission('hr.enabled')) {
            const hrSubmenu = [];
            if (hasPermission('hr.paystubs')) hrSubmenu.push({ name: 'Paystubs', href: '/paystub-settings' });
            if (hasPermission('hr.employees')) hrSubmenu.push({ name: 'Employees', href: '/employee-management' });
            if (hasPermission('hr.timesheets')) hrSubmenu.push({ name: 'Timesheets', href: '/timesheet-management' });
            
            if (hrSubmenu.length > 0) {
              roleDropdown.push({
                name: 'HR',
                submenu: hrSubmenu
              });
            }
          }
          
          // System Settings menu - check both feature flag and permissions
          if (system_settings_enabled && hasPermission('system_settings.enabled')) {
            const systemSettingsSubmenu = [];
            if (hasPermission('system_settings.cms')) systemSettingsSubmenu.push({ name: 'CMS', href: '/cms-settings' });
            if (hasPermission('system_settings.sms')) systemSettingsSubmenu.push({ name: 'SMS', href: '/sms-configuration' });
            if (hasPermission('system_settings.email')) systemSettingsSubmenu.push({ name: 'Email', href: '/email-configuration' });
            if (hasPermission('system_settings.users')) systemSettingsSubmenu.push({ name: 'Users', href: '/user-management' });
            if (hasPermission('system_settings.payments')) systemSettingsSubmenu.push({ name: 'Payments', href: '/payment-settings' });
            
            if (systemSettingsSubmenu.length > 0) {
              roleDropdown.push({ 
                name: 'System Settings',
                submenu: systemSettingsSubmenu
              });
            }
          }
          
          // Only show Online Ordering if enabled in settings and has permission
          if (online_ordering_enabled && hasPermission('online_ordering.enabled')) {
            const onlineOrderingSubmenu = [];
            
            // Online sub-section (Order Online, Order at Table, Pay at Table, Reserve a Table)
            const onlineSubmenu = [];
            if (hasPermission('online_ordering.order_online')) onlineSubmenu.push({ name: 'Order Online', href: '/order-online' });
            if (hasPermission('online_ordering.order_online')) onlineSubmenu.push({ name: 'Order at Table', href: '/order-at-table' });
            if (hasPermission('online_ordering.order_online')) onlineSubmenu.push({ name: 'Pay at Table', href: '/pay-at-table' });
            onlineSubmenu.push({ name: 'Reserve a Table', href: '/reserve-table' });
            
            if (onlineSubmenu.length > 0) {
              onlineOrderingSubmenu.push({
                name: 'Online',
                submenu: onlineSubmenu
              });
            }
            
            // Manage sub-section (Manage Orders, Manage Reservations, Manage Bargains)
            const manageSubmenu = [];
            if (hasPermission('online_ordering.manage_orders')) manageSubmenu.push({ name: 'Manage Orders', href: '/manage-orders' });
            if (hasPermission('online_ordering.manage_orders')) manageSubmenu.push({ name: 'Manage Reservations', href: '/manage-table-reservations' });
            if (hasPermission('online_ordering.manage_orders')) manageSubmenu.push({ name: 'Manage Bargains', href: '/manage-bargains' });
            
            if (manageSubmenu.length > 0) {
              onlineOrderingSubmenu.push({
                name: 'Manage',
                submenu: manageSubmenu
              });
            }
            
            // Configure sub-section (Configure Products, Configure Tables, Configure Order Types)
            const configureSubmenu = [];
            if (hasPermission('online_ordering.manage_products')) configureSubmenu.push({ name: 'Configure Products', href: '/menu-management' });
            if (hasPermission('online_ordering.manage_orders')) configureSubmenu.push({ name: 'Configure Tables', href: '/manage-tables' });
            if (hasPermission('online_ordering.configure_order_types')) configureSubmenu.push({ name: 'Configure Order Types', href: '/ecommerce-settings' });
            
            if (configureSubmenu.length > 0) {
              onlineOrderingSubmenu.push({
                name: 'Configure',
                submenu: configureSubmenu
              });
            }
            
            if (onlineOrderingSubmenu.length > 0) {
              roleDropdown.push({ 
                name: 'Online Ordering',
                submenu: onlineOrderingSubmenu
              });
            }
          }
          
          // Only show Records if enabled (Manager) and has permission
          if (records_enabled && hasPermission('records.enabled')) {
            const recordsSubmenu = [];
            if (hasPermission('records.client')) recordsSubmenu.push({ name: 'Client', href: '/client-records' });
            if (hasPermission('records.hospital')) recordsSubmenu.push({ name: 'Hospital', href: '/hospital-records' });
            
            if (recordsSubmenu.length > 0) {
              roleDropdown.push({
                name: 'Records',
                submenu: recordsSubmenu
              });
            }
          }
        
        // All menus above are permission-based and work for any custom role
        
        // Register User is only available for Admin users and when registration is enabled
        if (user.role === 'admin' && registration_enabled) {
          roleDropdown.push({ name: 'Register User', href: '/register' });
        }
      // REMOVED: Old owner-specific hardcoded block - all menus now permission-based
      
      // REMOVED: Legacy hardcoded Technician and User blocks
      // All users now use custom roles with permissions from Manage Roles
      // Navigation is built purely from permission checks above
      
      // Always add logout for logged-in users
      if (roleDropdown.length > 0) {
        roleDropdown.push({ name: 'Logout', action: 'logout', className: 'border-t border-gray-200 pt-2' });
      }
      
      // Determine navigation label based on user role
      // Prefer custom role name, then fall back to base role
      let navigationLabel;
      if (user.role_name) {
        // User has a custom role assigned
        navigationLabel = user.role_name;
      } else {
        // Fall back to base role
        switch (user.role) {
          case 'admin':
            navigationLabel = 'Admin';
            break;
          case 'owner':
            navigationLabel = 'Owner';
            break;
          case 'manager':
            navigationLabel = 'Manager';
            break;
          case 'technician':
            navigationLabel = 'Technician';
            break;
          case 'service_provider':
            navigationLabel = 'Service Provider';
            break;
          default:
            navigationLabel = 'User';
            break;
        }
      }
      
      // Only show role dropdown if there are items
      if (roleDropdown.length > 0) {
        baseNavigation.push({
          name: navigationLabel,
          dropdown: roleDropdown
        });
      }
    }

    // Add AI menu for users with AI features enabled
    if (user && ai_costs_enabled && hasPermission('ai_features.enabled')) {
      const aiDropdown = [];
      
      // AI Chatbot
      if (hasPermission('ai_features.chatbot')) {
        aiDropdown.push({ name: 'AI Chatbot', href: '/ai-chatbot' });
      }
      
      // AI Agents Dashboard
      if (hasPermission('ai_features.dashboard')) {
        aiDropdown.push({ name: 'AI Agents', href: '/ai-agents-dashboard' });
      }
      
      // AI Workflow submenu
      const workflowSubmenu = [];
      if (hasPermission('ai_features.in_review')) {
        workflowSubmenu.push({ name: 'In Review', href: '/ai-in-review' });
      }
      if (hasPermission('ai_features.ready_to_publish')) {
        workflowSubmenu.push({ name: 'Ready to Publish', href: '/ai-ready-to-publish' });
      }
      if (hasPermission('ai_features.published_posts')) {
        workflowSubmenu.push({ name: 'Published', href: '/ai-published-posts' });
      }
      
      if (workflowSubmenu.length > 0) {
        aiDropdown.push({ 
          name: 'AI Workflow', 
          submenu: workflowSubmenu
        });
      }
      
      // AI Settings (only for owners and admins or users with permission)
      if (hasPermission('ai_features.settings')) {
        aiDropdown.push({ name: 'AI Settings', href: '/ai-settings' });
      }
      
      // AI Costs
      if (hasPermission('ai_features.costs')) {
        aiDropdown.push({ name: 'AI Costs', href: '/ai-costs' });
      }

      // Only add AI menu if there are items
      if (aiDropdown.length > 0) {
        baseNavigation.push({
          name: 'AI',
          dropdown: aiDropdown
        });
      }
    }

    // Add Contact Us
    baseNavigation.push({
      name: 'Contact',
      dropdown: [
        { name: 'Reach Us', href: '/reach-us' },
        { name: 'Our Hours', href: '/our-hours' },
        { name: 'Message Us', href: '/message-us' }
      ]
    });

    return baseNavigation;
  };

  const navigation = getNavigation();

  const isActive = (href) => location.pathname === href;

  const isNavItemActive = (itemName) => {
    switch (itemName) {
      case 'Home':
        return location.pathname === '/';

      case 'Urgent Care':
        return location.pathname === '/urgent-care';
        
      case 'Services':
        return location.pathname.includes('/services') || 
               location.pathname.includes('/dog-') || 
               location.pathname.includes('/cat-') || 
               (location.pathname.includes('/pet-') && !location.pathname.includes('/pet-insurance-payments')) || 
               location.pathname.includes('/dental-') || 
               location.pathname.includes('/ultrasound-') || 
               location.pathname.includes('/veterinary-') || 
               location.pathname.includes('/digital-') || 
               location.pathname.includes('/preventive-') || 
               location.pathname.includes('/blocked-') || 
               location.pathname.includes('/foreign-') ||
               location.pathname.includes('/end-of-life-care') ||
               location.pathname.includes('/on-site-pharmacy') ||
               location.pathname.includes('/pet-travel-certificates');
      
      case 'About':
        return (location.pathname.includes('/our-') && !location.pathname.includes('/our-hours')) || 
               location.pathname.includes('/about') || 
               location.pathname.includes('/gallery') ||
               location.pathname.includes('/review-us');
      
      case 'Contact':
        return location.pathname.includes('/reach-us') || 
               location.pathname.includes('/message-us') || 
               location.pathname.includes('/contact') ||
               location.pathname.includes('/our-hours');
      
      case 'Patient':
        return location.pathname.includes('/forms') || 
               location.pathname.includes('/your-first-visit') ||
               location.pathname.includes('/pet-insurance-payments');
      
      case 'Admin':
      case 'Manager':
      case 'Technician':
      case 'User':
        return location.pathname.includes('/urgent-care-appointments') ||
               location.pathname.includes('/configure-hours') ||
               location.pathname.includes('/reviews') ||
               location.pathname.includes('/business-info') ||
               location.pathname.includes('/photo-management') ||
               location.pathname.includes('/user-management') ||
               location.pathname.includes('/register');  // Updated from /register-user
      
      case 'AI':
        return location.pathname.startsWith('/ai-');

      default:
        return false;
    }
  };

  return (
    <>
      <style>{`
        .submenu-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .submenu-scrollbar::-webkit-scrollbar-track {
          background: #f3f4f6;
          border-radius: 3px;
        }
        .submenu-scrollbar::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 3px;
        }
        .submenu-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
        @keyframes marquee-left {
          0% { transform: translateX(100%); }
          100% { transform: translateX(-100%); }
        }
        @keyframes marquee-right {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
        @keyframes fade-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
        .animate-marquee-left {
          animation: marquee-left 15s linear infinite;
        }
        .animate-marquee-right {
          animation: marquee-right 15s linear infinite;
        }
        .animate-blink {
          animation: blink 1.5s ease-in-out infinite;
        }
        .animate-fade-pulse {
          animation: fade-pulse 2s ease-in-out infinite;
        }
      `}</style>
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200">
        {/* Notification Bar - Controlled by notification bar settings */}
        {notificationBarSettings?.enabled && (
          <>
            {/* Custom Message Mode */}
            {notificationBarSettings.mode === 'custom_message' && notificationBarSettings.message_text && (
              <div 
                className="py-2 px-4 text-center overflow-hidden"
                style={{ 
                  backgroundColor: notificationBarSettings.background_color || '#0891b2', 
                  color: notificationBarSettings.text_color || '#ffffff' 
                }}
              >
                <div className="max-w-6xl mx-auto flex items-center justify-center gap-4">
                  <span className={`whitespace-nowrap text-sm font-medium ${
                    notificationBarSettings.animation_type === 'scroll_left' ? 'animate-marquee-left' :
                    notificationBarSettings.animation_type === 'scroll_right' ? 'animate-marquee-right' :
                    notificationBarSettings.animation_type === 'blink' ? 'animate-blink' :
                    notificationBarSettings.animation_type === 'fade' ? 'animate-fade-pulse' : ''
                  }`}>
                    {notificationBarSettings.message_text}
                  </span>
                  {notificationBarSettings.show_link_button && notificationBarSettings.link_button_text && (
                    <a 
                      href={notificationBarSettings.link_button_url || '#'}
                      className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded text-sm font-medium whitespace-nowrap transition-colors"
                    >
                      {notificationBarSettings.link_button_text}
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Business Info Mode */}
            {notificationBarSettings.mode === 'business_info' && (
              <div 
                className="text-white py-2" 
                style={{ backgroundColor: notificationBarSettings.background_color || 'var(--theme-primary)' }}
              >
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                  {/* Desktop Layout */}
                  <div className="hidden lg:flex justify-between items-center text-sm">
                    <div className="flex items-center space-x-4">
                      {notificationBarSettings.show_phone && businessInfo?.phone && (
                        <a 
                          href={`tel:${businessInfo.phone}`}
                          className="flex items-center space-x-1 hover:opacity-80 transition-opacity cursor-pointer"
                          title="Click to call"
                        >
                          <Phone className="h-4 w-4" />
                          <span>{businessInfo.phone}</span>
                        </a>
                      )}
                      {notificationBarSettings.show_address && businessInfo?.address && (
                        <a 
                          href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(businessInfo.address)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-1 hover:opacity-80 transition-opacity cursor-pointer"
                          title="Click for directions"
                        >
                          <MapPin className="h-4 w-4" />
                          <span className="truncate">{getShortAddress()}</span>
                        </a>
                      )}
                    </div>
                    {notificationBarSettings.show_hours_status && (
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                          <Clock className="h-4 w-4" />
                          <span>General Practice:</span>
                          <span style={getStatusStyling(getGeneralPracticeStatus())}>
                            {getGeneralPracticeStatus()}
                          </span>
                        </div>
                        {urgent_care_enabled && (
                          <div className="flex items-center space-x-2">
                            <Clock className="h-4 w-4" />
                            <span>Urgent Care:</span>
                            <span style={getStatusStyling(getUrgentCareStatus())}>
                              {getUrgentCareStatus()}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Mobile Layout */}
                  <div className="lg:hidden">
                    {/* Row 1: Phone (left) and GP Status (right) */}
                    <div className="flex justify-between items-center text-xs mb-0.5">
                      {notificationBarSettings.show_phone && businessInfo?.phone && (
                        <a 
                          href={`tel:${businessInfo.phone}`}
                          className="flex items-center space-x-1 hover:opacity-80 transition-opacity"
                          title="Tap to call"
                        >
                          <Phone className="h-3 w-3" />
                          <span>{businessInfo.phone}</span>
                        </a>
                      )}
                      {notificationBarSettings.show_hours_status && (
                        <div className="flex items-center space-x-1">
                          <Clock className="h-3 w-3" />
                          <span>General Practice:</span>
                          <span style={getStatusStyling(getGeneralPracticeStatus())}>
                            {getGeneralPracticeStatus()}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {/* Row 2: Address (left) and UC Status (right) */}
                    <div className="flex justify-between items-center text-xs">
                      {notificationBarSettings.show_address && businessInfo?.address && (
                        <a 
                          href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(businessInfo.address)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-1 hover:opacity-80 transition-opacity"
                          title="Tap for directions"
                        >
                          <MapPin className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate text-xs">{getShortAddress()}</span>
                        </a>
                      )}
                      {urgent_care_enabled && notificationBarSettings.show_hours_status && (
                        <div className="flex items-center space-x-1">
                          <Clock className="h-3 w-3" />
                          <span>Urgent Care:</span>
                          <span style={getStatusStyling(getUrgentCareStatus())}>
                            {getUrgentCareStatus()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

      {/* Main Header */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Logo Left Layout (Default) */}
        {(!headerConfig?.layout || headerConfig?.layout === 'logo-left') && (
          <div className="flex justify-between items-center py-3">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              {renderLogo('normal')}
              {renderBusinessText()}
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex space-x-4">
              {navigation.map((item) => {
                if (item.name === 'Home' && location.pathname === '/') {
                  return null;
                }
                
                return (
                  <div key={item.name} className="relative">
                    {item.dropdown ? (
                      <div 
                        className="relative"
                        onMouseEnter={() => setActiveDropdown(item.name)}
                        onMouseLeave={() => {
                          setTimeout(() => setActiveDropdown(null), 100);
                        }}
                      >
                        <button
                          onClick={() => setActiveDropdown(activeDropdown === item.name ? null : item.name)}
                          className={`flex items-center px-2 py-1 text-sm font-medium rounded-lg transition-all duration-200 ${
                            isNavItemActive(item.name)
                              ? 'text-gray-900' 
                              : 'text-gray-700 hover:text-gray-900'
                          }`}
                          style={{
                            backgroundColor: isNavItemActive(item.name) ? 'var(--theme-primary-light)' : 'transparent',
                            color: isNavItemActive(item.name) ? (headerConfig?.theme_color || 'var(--theme-primary)') : (headerConfig?.menu_text_color || undefined)
                          }}
                          onMouseEnter={(e) => {
                            if (!isNavItemActive(item.name)) {
                              e.target.style.color = headerConfig?.menu_hover_color || 'var(--theme-primary)';
                              e.target.style.backgroundColor = '#f0fdff';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isNavItemActive(item.name)) {
                              e.target.style.color = headerConfig?.menu_text_color || '#374151';
                              e.target.style.backgroundColor = 'transparent';
                            }
                          }}
                        >
                          {renderMenuContent(item.name, getNavItemIcon(item.name), true)}
                        </button>
                        
                        {/* Dropdown Menu */}
                        {activeDropdown === item.name && (
                          <div 
                            className="absolute top-full left-0 mt-0 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-[60]"
                            onMouseEnter={() => setActiveDropdown(item.name)}
                            onMouseLeave={() => {
                              setTimeout(() => {
                                setActiveDropdown(null);
                                setActiveSubmenu(null);
                                setOpenSubSubmenus(new Set());
                              }, 100);
                            }}
                          >
                            {item.dropdown.map((dropdownItem, index) => (
                              <div key={index} className={index < item.dropdown.length - 1 ? "border-b border-gray-100" : ""}>
                                {dropdownItem.submenu ? (
                                  <div 
                                    className="relative"
                                    onMouseEnter={() => {
                                      setActiveSubmenu(dropdownItem.name);
                                    }}
                                  >
                                    <div className="flex items-center justify-between px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
                                      <span className="font-medium">{dropdownItem.name}</span>
                                      <ChevronDown className="h-4 w-4 transform -rotate-90" />
                                    </div>
                                    {activeSubmenu === dropdownItem.name && (
                                      <>
                                        <div 
                                          className="absolute left-full top-0 ml-0 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-[70]"
                                          onMouseEnter={() => {
                                            setActiveSubmenu(dropdownItem.name);
                                          }}
                                        >
                                          {dropdownItem.submenu.map((subItem, subIndex) => (
                                            <div key={subIndex}>
                                              {subItem.submenu ? (
                                                <div>
                                                  <button
                                                    onClick={(e) => {
                                                      e.preventDefault();
                                                      e.stopPropagation();
                                                      toggleSubSubmenu(subItem.name);
                                                    }}
                                                    className="w-full flex items-center justify-between px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200"
                                                  >
                                                    <span>{subItem.name}</span>
                                                    <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${openSubSubmenus.has(subItem.name) ? 'rotate-180' : ''}`} />
                                                  </button>
                                                  
                                                  {openSubSubmenus.has(subItem.name) && (
                                                  <div className="bg-gray-50">
                                                    {subItem.submenu.map((thirdLevelItem, thirdIndex) => (
                                                      <Link
                                                        key={thirdIndex}
                                                        to={thirdLevelItem.href}
                                                        className="block pl-8 pr-4 py-2 text-sm text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors duration-200"
                                                        onClick={() => {
                                                          setActiveDropdown(null);
                                                          setActiveSubmenu(null);
                                                          setOpenSubSubmenus(new Set());
                                                        }}
                                                      >
                                                        {thirdLevelItem.name}
                                                      </Link>
                                                    ))}
                                                  </div>
                                                  )}
                                                </div>
                                              ) : (
                                                <Link
                                                  to={subItem.href}
                                                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors duration-200"
                                                  onClick={() => {
                                                    setActiveDropdown(null);
                                                    setActiveSubmenu(null);
                                                    setOpenSubSubmenus(new Set());
                                                  }}
                                                >
                                                  {subItem.name}
                                                </Link>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      </>
                                    )}
                                  </div>
                                ) : dropdownItem.action === 'logout' ? (
                                  <button
                                    onClick={() => {
                                      handleLogout();
                                      setActiveDropdown(null);
                                    }}
                                    onMouseEnter={() => {
                                      setActiveSubmenu(null);
                                      setOpenSubSubmenus(new Set());
                                    }}
                                    className={`block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-red-600 transition-colors duration-200 ${dropdownItem.className || ''}`}
                                  >
                                    {dropdownItem.name}
                                  </button>
                                ) : (
                                  <Link
                                    to={dropdownItem.href}
                                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors duration-200"
                                    onMouseEnter={() => {
                                      setActiveSubmenu(null);
                                      setOpenSubSubmenus(new Set());
                                    }}
                                    onClick={() => setActiveDropdown(null)}
                                  >
                                    {dropdownItem.name}
                                  </Link>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <Link
                        to={item.href}
                        className={`flex items-center px-2 py-1 text-sm font-medium rounded-lg transition-all duration-200 ${
                          isNavItemActive(item.name)
                            ? 'text-gray-900' 
                            : 'text-gray-700 hover:text-gray-900'
                        }`}
                        style={{
                          backgroundColor: isNavItemActive(item.name) ? 'var(--theme-primary-light)' : 'transparent',
                          color: isNavItemActive(item.name) ? (headerConfig?.theme_color || 'var(--theme-primary)') : (headerConfig?.menu_text_color || undefined)
                        }}
                        onMouseEnter={(e) => {
                          if (!isNavItemActive(item.name)) {
                            e.target.style.color = headerConfig?.menu_hover_color || 'var(--theme-primary)';
                            e.target.style.backgroundColor = '#f0fdff';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isNavItemActive(item.name)) {
                            e.target.style.color = headerConfig?.menu_text_color || '#374151';
                            e.target.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        {renderMenuContent(item.name, getNavItemIcon(item.name), false)}
                      </Link>
                    )}
                  </div>
                );
              })}
            </nav>

            {/* CTA Button */}
            {headerConfig?.cta_buttons?.length > 0 ? (
              <div className="hidden lg:flex items-center space-x-4">
                {headerConfig.cta_buttons.filter(btn => btn.enabled !== false).map((btn, index) => (
                  <Link
                    key={btn.id || index}
                    to={btn.url || '/'}
                    className="inline-flex items-center text-white px-6 py-2 rounded-lg font-medium transition-all duration-200 shadow-md hover:shadow-lg"
                    style={{ backgroundColor: btn.bg_color || headerConfig?.theme_color || 'var(--theme-primary)' }}
                  >
                    {btn.label}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                ))}
              </div>
            ) : urgent_care_enabled ? (
              <div className="hidden lg:flex items-center space-x-4">
                <Link
                  to="/urgent-care-booking"
                  className="inline-flex items-center text-white px-6 py-2 rounded-lg font-medium transition-all duration-200 shadow-md hover:shadow-lg"
                  style={{ backgroundColor: headerConfig?.theme_color || 'var(--theme-primary)' }}
                >
                  Check In Online
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            ) : (
              <div className="hidden lg:flex items-center space-x-4">
                <Link
                  to="/specialty-services"
                  className="inline-flex items-center text-white px-6 py-2 rounded-lg font-medium transition-all duration-200 shadow-md hover:shadow-lg"
                  style={{ backgroundColor: headerConfig?.theme_color || 'var(--theme-primary)' }}
                >
                  Specialty Care
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            )}

            {/* Mobile menu button */}
            <button
              onClick={() => {
                setIsMenuOpen(!isMenuOpen);
                if (isMenuOpen) {
                  setOpenMobileSubmenu(null);
                }
              }}
              className="lg:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors duration-200"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        )}

        {/* Logo Center Layout */}
        {headerConfig?.layout === 'logo-center' && (
          <div className="py-3">
            {/* Desktop Layout */}
            <div className="hidden lg:flex items-center justify-between">
              {/* Left Navigation */}
              <nav className="flex space-x-4 flex-1">
                {navigation.slice(0, Math.ceil(navigation.length / 2)).map((item) => {
                  if (item.name === 'Home' && location.pathname === '/') {
                    return null;
                  }
                  
                  return (
                    <div key={item.name} className="relative">
                      {item.dropdown ? (
                        <div 
                          className="relative"
                          onMouseEnter={() => setActiveDropdown(item.name)}
                          onMouseLeave={() => {
                            setTimeout(() => setActiveDropdown(null), 100);
                          }}
                        >
                          <button
                            onClick={() => setActiveDropdown(activeDropdown === item.name ? null : item.name)}
                            className="flex items-center px-2 py-1 text-sm font-medium rounded-lg transition-all duration-200"
                            style={{
                              color: headerConfig?.menu_text_color || '#374151'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.color = headerConfig?.menu_hover_color || 'var(--theme-primary)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.color = headerConfig?.menu_text_color || '#374151';
                            }}
                          >
                            {renderMenuContent(item.name, getNavItemIcon(item.name), true)}
                          </button>
                          
                          {activeDropdown === item.name && (
                            <div 
                              className="absolute top-full left-0 mt-0 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-[60]"
                              onMouseEnter={() => setActiveDropdown(item.name)}
                              onMouseLeave={() => {
                                setTimeout(() => {
                                  setActiveDropdown(null);
                                  setActiveSubmenu(null);
                                  setOpenSubSubmenus(new Set());
                                }, 100);
                              }}
                            >
                              {item.dropdown.map((dropdownItem, index) => (
                                <div key={index} className={index < item.dropdown.length - 1 ? "border-b border-gray-100" : ""}>
                                  {dropdownItem.submenu ? (
                                    <div 
                                      className="relative"
                                      onMouseEnter={() => setActiveSubmenu(dropdownItem.name)}
                                    >
                                      <div className="flex items-center justify-between px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
                                        <span className="font-medium">{dropdownItem.name}</span>
                                        <ChevronDown className="h-4 w-4 transform -rotate-90" />
                                      </div>
                                      {activeSubmenu === dropdownItem.name && (
                                        <div 
                                          className="absolute left-full top-0 ml-0 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-[70]"
                                          onMouseEnter={() => setActiveSubmenu(dropdownItem.name)}
                                        >
                                          {dropdownItem.submenu.map((subItem, subIndex) => (
                                            <div key={subIndex}>
                                              {subItem.submenu ? (
                                                <div>
                                                  <button
                                                    onClick={(e) => {
                                                      e.preventDefault();
                                                      e.stopPropagation();
                                                      toggleSubSubmenu(subItem.name);
                                                    }}
                                                    className="w-full flex items-center justify-between px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200"
                                                  >
                                                    <span>{subItem.name}</span>
                                                    <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${openSubSubmenus.has(subItem.name) ? 'rotate-180' : ''}`} />
                                                  </button>
                                                  {openSubSubmenus.has(subItem.name) && (
                                                    <div className="bg-gray-50">
                                                      {subItem.submenu.map((thirdLevelItem, thirdIndex) => (
                                                        <Link
                                                          key={thirdIndex}
                                                          to={thirdLevelItem.href}
                                                          className="block pl-8 pr-4 py-2 text-sm text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors duration-200"
                                                          onClick={() => {
                                                            setActiveDropdown(null);
                                                            setActiveSubmenu(null);
                                                            setOpenSubSubmenus(new Set());
                                                          }}
                                                        >
                                                          {thirdLevelItem.name}
                                                        </Link>
                                                      ))}
                                                    </div>
                                                  )}
                                                </div>
                                              ) : (
                                                <Link
                                                  to={subItem.href}
                                                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors duration-200"
                                                  onClick={() => {
                                                    setActiveDropdown(null);
                                                    setActiveSubmenu(null);
                                                    setOpenSubSubmenus(new Set());
                                                  }}
                                                >
                                                  {subItem.name}
                                                </Link>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  ) : dropdownItem.action === 'logout' ? (
                                    <button
                                      onClick={() => {
                                        handleLogout();
                                        setActiveDropdown(null);
                                      }}
                                      onMouseEnter={() => {
                                        setActiveSubmenu(null);
                                        setOpenSubSubmenus(new Set());
                                      }}
                                      className={`block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-red-600 transition-colors duration-200 ${dropdownItem.className || ''}`}
                                    >
                                      {dropdownItem.name}
                                    </button>
                                  ) : (
                                    <Link
                                      to={dropdownItem.href || '#'}
                                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors duration-200"
                                      onMouseEnter={() => {
                                        setActiveSubmenu(null);
                                        setOpenSubSubmenus(new Set());
                                      }}
                                      onClick={() => setActiveDropdown(null)}
                                    >
                                      {dropdownItem.name}
                                    </Link>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <Link
                          to={item.href}
                          className="flex items-center px-2 py-1 text-sm font-medium rounded-lg transition-all duration-200"
                          style={{ color: headerConfig?.menu_text_color || '#374151' }}
                          onMouseEnter={(e) => {
                            e.target.style.color = headerConfig?.menu_hover_color || 'var(--theme-primary)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.color = headerConfig?.menu_text_color || '#374151';
                          }}
                        >
                          {renderMenuContent(item.name, getNavItemIcon(item.name), false)}
                        </Link>
                      )}
                    </div>
                  );
                })}
              </nav>

              {/* Center Logo */}
              <Link to="/" className="flex items-center space-x-2 mx-8">
                {renderLogo('large')}
                {renderBusinessText()}
              </Link>

              {/* Right Navigation */}
              <nav className="flex space-x-4 flex-1 justify-end">
                {navigation.slice(Math.ceil(navigation.length / 2)).map((item) => {
                  if (item.name === 'Home' && location.pathname === '/') {
                    return null;
                  }
                  
                  return (
                    <div key={item.name} className="relative">
                      {item.dropdown ? (
                        <div 
                          className="relative"
                          onMouseEnter={() => setActiveDropdown(item.name)}
                          onMouseLeave={() => {
                            setTimeout(() => setActiveDropdown(null), 100);
                          }}
                        >
                          <button
                            onClick={() => setActiveDropdown(activeDropdown === item.name ? null : item.name)}
                            className="flex items-center px-2 py-1 text-sm font-medium rounded-lg transition-all duration-200"
                            style={{ color: headerConfig?.menu_text_color || '#374151' }}
                            onMouseEnter={(e) => {
                              e.target.style.color = headerConfig?.menu_hover_color || 'var(--theme-primary)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.color = headerConfig?.menu_text_color || '#374151';
                            }}
                          >
                            {renderMenuContent(item.name, getNavItemIcon(item.name), true)}
                          </button>
                          
                          {activeDropdown === item.name && (
                            <div 
                              className="absolute top-full right-0 mt-0 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-[60]"
                              onMouseEnter={() => setActiveDropdown(item.name)}
                              onMouseLeave={() => {
                                setTimeout(() => {
                                  setActiveDropdown(null);
                                  setActiveSubmenu(null);
                                  setOpenSubSubmenus(new Set());
                                }, 100);
                              }}
                            >
                              {item.dropdown.map((dropdownItem, index) => (
                                <div key={index} className={index < item.dropdown.length - 1 ? "border-b border-gray-100" : ""}>
                                  {dropdownItem.submenu ? (
                                    <div 
                                      className="relative"
                                      onMouseEnter={() => setActiveSubmenu(dropdownItem.name)}
                                    >
                                      <div className="flex items-center justify-between px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
                                        <span className="font-medium">{dropdownItem.name}</span>
                                        <ChevronDown className="h-4 w-4 transform -rotate-90" />
                                      </div>
                                      {activeSubmenu === dropdownItem.name && (
                                        <div 
                                          className="absolute right-full top-0 mr-0 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-[70]"
                                          onMouseEnter={() => setActiveSubmenu(dropdownItem.name)}
                                        >
                                          {dropdownItem.submenu.map((subItem, subIndex) => (
                                            <div key={subIndex}>
                                              {subItem.submenu ? (
                                                <div>
                                                  <button
                                                    onClick={(e) => {
                                                      e.preventDefault();
                                                      e.stopPropagation();
                                                      toggleSubSubmenu(subItem.name);
                                                    }}
                                                    className="w-full flex items-center justify-between px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200"
                                                  >
                                                    <span>{subItem.name}</span>
                                                    <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${openSubSubmenus.has(subItem.name) ? 'rotate-180' : ''}`} />
                                                  </button>
                                                  {openSubSubmenus.has(subItem.name) && (
                                                    <div className="bg-gray-50">
                                                      {subItem.submenu.map((thirdLevelItem, thirdIndex) => (
                                                        <Link
                                                          key={thirdIndex}
                                                          to={thirdLevelItem.href}
                                                          className="block pl-8 pr-4 py-2 text-sm text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors duration-200"
                                                          onClick={() => {
                                                            setActiveDropdown(null);
                                                            setActiveSubmenu(null);
                                                            setOpenSubSubmenus(new Set());
                                                          }}
                                                        >
                                                          {thirdLevelItem.name}
                                                        </Link>
                                                      ))}
                                                    </div>
                                                  )}
                                                </div>
                                              ) : (
                                                <Link
                                                  to={subItem.href}
                                                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors duration-200"
                                                  onClick={() => {
                                                    setActiveDropdown(null);
                                                    setActiveSubmenu(null);
                                                    setOpenSubSubmenus(new Set());
                                                  }}
                                                >
                                                  {subItem.name}
                                                </Link>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  ) : dropdownItem.action === 'logout' ? (
                                    <button
                                      onClick={() => {
                                        handleLogout();
                                        setActiveDropdown(null);
                                      }}
                                      onMouseEnter={() => {
                                        setActiveSubmenu(null);
                                        setOpenSubSubmenus(new Set());
                                      }}
                                      className={`block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-red-600 transition-colors duration-200 ${dropdownItem.className || ''}`}
                                    >
                                      {dropdownItem.name}
                                    </button>
                                  ) : (
                                    <Link
                                      to={dropdownItem.href || '#'}
                                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors duration-200"
                                      onMouseEnter={() => {
                                        setActiveSubmenu(null);
                                        setOpenSubSubmenus(new Set());
                                      }}
                                      onClick={() => setActiveDropdown(null)}
                                    >
                                      {dropdownItem.name}
                                    </Link>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <Link
                          to={item.href}
                          className="flex items-center px-2 py-1 text-sm font-medium rounded-lg transition-all duration-200"
                          style={{ color: headerConfig?.menu_text_color || '#374151' }}
                          onMouseEnter={(e) => {
                            e.target.style.color = headerConfig?.menu_hover_color || 'var(--theme-primary)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.color = headerConfig?.menu_text_color || '#374151';
                          }}
                        >
                          {renderMenuContent(item.name, getNavItemIcon(item.name), false)}
                        </Link>
                      )}
                    </div>
                  );
                })}
                
                {/* CTA Buttons */}
                {headerConfig?.cta_buttons?.filter(btn => btn.enabled !== false).map((btn, index) => (
                  <Link
                    key={btn.id || index}
                    to={btn.url || '/'}
                    className="inline-flex items-center text-white px-4 py-1.5 rounded-lg font-medium transition-all duration-200 shadow-md hover:shadow-lg text-sm"
                    style={{ backgroundColor: btn.bg_color || headerConfig?.theme_color || 'var(--theme-primary)' }}
                  >
                    {btn.label}
                    <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                  </Link>
                ))}
              </nav>
            </div>

            {/* Mobile Layout for Logo Center */}
            <div className="lg:hidden flex justify-between items-center">
              <Link to="/" className="flex items-center space-x-2">
                {renderLogo('normal')}
                {renderBusinessText()}
              </Link>
              <button
                onClick={() => {
                  setIsMenuOpen(!isMenuOpen);
                  if (isMenuOpen) {
                    setOpenMobileSubmenu(null);
                  }
                }}
                className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors duration-200"
              >
                {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200 shadow-lg">
          <div className="px-4 py-3 space-y-2 max-h-96 overflow-y-auto">
            {navigation.map((item) => {
              // Hide Home link when on home page
              if (item.name === 'Home' && location.pathname === '/') {
                return null;
              }
              
              return (
                <div key={item.name}>
                  {item.dropdown ? (
                    <div>
                      <button
                        onClick={() => toggleMobileSubmenu(item.name)}
                        className={`flex items-center justify-between w-full px-3 py-2 text-base font-medium rounded-lg transition-all duration-200 ${
                          isNavItemActive(item.name) || openMobileSubmenu === item.name
                            ? 'text-gray-900'
                            : 'text-gray-700 hover:text-gray-900'
                        }`}
                        style={{
                          backgroundColor: (isNavItemActive(item.name) || openMobileSubmenu === item.name) ? 'var(--theme-primary-light)' : undefined,
                          color: (isNavItemActive(item.name) || openMobileSubmenu === item.name) ? 'var(--theme-primary)' : undefined
                        }}
                      >
                        <span>{item.name}</span>
                        {openMobileSubmenu === item.name ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </button>
                      
                      {/* Mobile Dropdown Items with Slide Animation */}
                      <div 
                        className={`overflow-hidden transition-all duration-300 ease-in-out ${
                          openMobileSubmenu === item.name ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
                        }`}
                      >
                        <div className="ml-4 mt-2 space-y-2 pb-2">
                          {item.dropdown.map((dropdownItem, index) => (
                            <div key={index}>
                              {dropdownItem.submenu ? (
                                <div>
                                  <div className="px-3 py-2 text-sm font-medium text-gray-600 bg-gray-50 rounded-md">
                                    {dropdownItem.name}
                                  </div>
                                  <div className="submenu-scrollbar ml-4 mt-1 space-y-1 max-h-[400px] overflow-y-auto">
                                    {dropdownItem.submenu.map((subItem, subIndex) => (
                                      <div key={subIndex}>
                                        {subItem.submenu ? (
                                          /* Collapsible third-level menu for mobile */
                                          <div>
                                            <button
                                              onClick={() => toggleMobileSubSubmenu(subItem.name)}
                                              className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-all duration-200"
                                            >
                                              <span>{subItem.name}</span>
                                              <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${openMobileSubSubmenus.has(subItem.name) ? 'rotate-180' : ''}`} />
                                            </button>
                                            {openMobileSubSubmenus.has(subItem.name) && (
                                              <div className="ml-4 mt-1 space-y-1 bg-gray-50 rounded-md">
                                                {subItem.submenu.map((thirdLevelItem, thirdIndex) => (
                                                  <Link
                                                    key={thirdIndex}
                                                    to={thirdLevelItem.href}
                                                    onClick={() => {
                                                      setIsMenuOpen(false);
                                                      setOpenMobileSubmenu(null);
                                                      setOpenMobileSubSubmenus(new Set());
                                                    }}
                                                    className="block px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-all duration-200"
                                                    style={{
                                                      backgroundColor: location.pathname === thirdLevelItem.href ? 'var(--theme-primary)' : undefined,
                                                      color: location.pathname === thirdLevelItem.href ? 'white' : undefined
                                                    }}
                                                  >
                                                    {thirdLevelItem.name}
                                                  </Link>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        ) : (
                                          <Link
                                            to={subItem.href}
                                            onClick={() => {
                                              setIsMenuOpen(false);
                                              setOpenMobileSubmenu(null);
                                              setOpenMobileSubSubmenus(new Set());
                                            }}
                                            className="block px-3 py-2 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-all duration-200"
                                            style={{
                                              backgroundColor: location.pathname === subItem.href ? 'var(--theme-primary)' : undefined,
                                              color: location.pathname === subItem.href ? 'white' : undefined
                                            }}
                                          >
                                            {subItem.name}
                                          </Link>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ) : dropdownItem.action === 'logout' ? (
                                <button
                                  onClick={() => {
                                    handleLogout();
                                    setIsMenuOpen(false);
                                    setOpenMobileSubmenu(null);
                                  }}
                                  className={`block w-full text-left px-3 py-2 text-sm text-gray-700 hover:text-red-600 rounded-md transition-colors duration-200 ${dropdownItem.className || ''}`}
                                >
                                  {dropdownItem.name}
                                </button>
                              ) : (
                                <Link
                                  to={dropdownItem.href}
                                  onClick={() => {
                                    setIsMenuOpen(false);
                                    setOpenMobileSubmenu(null);
                                  }}
                                  className="block px-3 py-2 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-all duration-200"
                                  style={{
                                    backgroundColor: location.pathname === dropdownItem.href ? 'var(--theme-primary)' : undefined,
                                    color: location.pathname === dropdownItem.href ? 'white' : undefined
                                  }}
                                >
                                  {dropdownItem.name}
                                </Link>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      onClick={() => {
                        setIsMenuOpen(false);
                        setOpenMobileSubmenu(null);
                      }}
                      className={`block px-3 py-2 text-base font-medium rounded-lg transition-all duration-200 ${
                        isNavItemActive(item.name)
                          ? 'text-gray-900'
                          : 'text-gray-700 hover:text-gray-900'
                      }`}
                      style={{
                        backgroundColor: isNavItemActive(item.name) ? 'var(--theme-primary-light)' : undefined,
                        color: isNavItemActive(item.name) ? 'var(--theme-primary)' : undefined
                      }}
                      onMouseEnter={(e) => {
                        if (!isNavItemActive(item.name)) {
                          e.target.style.color = 'var(--theme-primary)';
                          e.target.style.backgroundColor = '#f9fafb';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isNavItemActive(item.name)) {
                          e.target.style.color = '#374151';
                          e.target.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              );
            })}
            {urgent_care_enabled ? (
              <Link
                to="/urgent-care-booking"
                onClick={() => setIsMenuOpen(false)}
                className="inline-flex items-center justify-center w-full text-center text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200"
                style={{ backgroundColor: 'var(--theme-primary)' }}
                onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--theme-primary-hover)'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
              >
                Check In Online
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            ) : (
              <Link
                to="/specialty-services"
                onClick={() => setIsMenuOpen(false)}
                className="inline-flex items-center justify-center w-full text-center text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200"
                style={{ backgroundColor: 'var(--theme-primary)' }}
                onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--theme-primary-hover)'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
              >
                Specialty Care
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
    </>
  );
};

export default Header;